/**
 * SessionAnalyzer.jsx â€” FEAT-SESSIONS-001 (Marketing â†’ Session Analyzer)
 * Purpose:  Rules that run when a web session ENDS (30-min inactivity):
 *           OR groups of AND-ed event conditions (occurred / did not occur,
 *           with optional property filters) â†’ trigger an xFlow Flow.
 * Backend:  RRS /session-analyzer/* (rules CRUD, triggers, run-now);
 *           event/attribute pickers reuse /fetch-unique-events and
 *           /fetch-unique-attributes; flow picker uses /flow/list.
 * Security: tenant pinned server-side from x-api-key (client never sends
 *           customer_id).
 */
import React, { useState, useEffect, useCallback } from 'react';
import {
    Box, Flex, Button, Text, Select, Input, Textarea, IconButton,
    Table, Thead, Tbody, Tr, Th, Td, TableContainer, Tabs, TabList, Tab,
    TabPanels, TabPanel, Modal, ModalOverlay, ModalContent, ModalHeader,
    ModalBody, ModalFooter, ModalCloseButton, useDisclosure, useToast,
    Spinner, HStack, VStack, Tag, Tooltip, Divider,
    AlertDialog, AlertDialogOverlay, AlertDialogContent, AlertDialogHeader,
    AlertDialogBody, AlertDialogFooter,
    Drawer, DrawerOverlay, DrawerContent, DrawerHeader, DrawerBody, DrawerCloseButton,
} from '@chakra-ui/react';
import { FiPlus, FiTrash2, FiEdit2, FiPlay, FiPause, FiRefreshCw, FiZap, FiEye, FiExternalLink } from 'react-icons/fi';
import { useNavigate } from 'react-router-dom';
import Pagination from 'react-js-pagination';
import BoxWrapper from '../../components/BoxWrapper/BoxWrapper';
import Header from '../../components/Header';
import API from '../../helpers/axiosInstance';
import { getApiHeaders } from '../../utils/DefaultFunction';

const OPERATORS = [
    { value: 'equals', label: 'equals' },
    { value: 'not_equals', label: 'not equals' },
    { value: 'contains', label: 'contains' },
    { value: 'greater_than', label: 'greater than' },
    { value: 'less_than', label: 'less than' },
];

const SESSION_METRIC_OPTIONS = [
    { value: 'duration_minutes', label: 'Duration (minutes)', type: 'numeric' },
    { value: 'total_events', label: 'Total events', type: 'numeric' },
    { value: 'entry_event', label: 'Entry event', type: 'string' },
    { value: 'exit_event', label: 'Exit event', type: 'string' },
    { value: 'channel', label: 'Channel', type: 'string' },
];
const metricType = (m) => (SESSION_METRIC_OPTIONS.find((o) => o.value === m) || {}).type || 'string';
const metricOperators = (m) => OPERATORS.filter((op) =>
    metricType(m) === 'numeric' ? op.value !== 'contains' : !['greater_than', 'less_than'].includes(op.value)
);

// Auto-tracked session fields available as flow parameters (session_field source).
const SESSION_FIELD_OPTIONS = [
    { value: 'distinct_id', label: 'Subscriber ID (distinct_id)' },
    { value: 'contact', label: 'Contact number' },
    { value: 'session_id', label: 'Session ID' },
    { value: 'channel', label: 'Channel' },
    { value: 'country', label: 'Country' },
];

const emptyCondition = () => ({ event_name: '', occurrence: 'present', filters: [] });
const emptyGroup = () => ({ conditions: [emptyCondition()], session_conditions: [], flow_id: '', flow_name: '', parameters: [] });
const emptyRule = () => ({
    name: '', description: '', audience: 'all', status: 'active',
    groups: [emptyGroup()],
});

/**
 * Accent colour on this page is `colorScheme="brand"` / `brand.50`â€“`brand.900`, which
 * App.js maps to the env-driven ramp (`PRIMARY_SCALE` from REACT_APP_PRIMARY_COLOR).
 * It used to be Chakra's raw `purple` (accent) and `teal` (dry-run actions) plus a
 * hardcoded #805AD5, none of which move with the brand â€” so on a rebranded build the
 * whole feature stayed purple. Do not reintroduce a literal palette or hex here; for
 * anything Chakra style props cannot reach, use `var(--brand-primary)`.
 *
 * STATUS_COLORS below is deliberately NOT branded: these are semantic outcome colours
 * (fired / failed / skipped), and they must stay green/red/orange under every brand.
 */
const STATUS_COLORS = { fired: 'green', failed: 'red', skipped: 'orange', pending: 'gray' };

function fmtTs(v) {
    if (!v) return 'â€”';
    const d = new Date(v);
    return Number.isFinite(d.getTime()) ? d.toLocaleString() : String(v);
}

/* â”€â”€â”€ Condition row â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€ */
function ConditionRow({ condition, events, attributes, onChange, onRemove, onNeedAttributes, removable }) {
    const update = (patch) => onChange({ ...condition, ...patch });

    const updateFilter = (idx, patch) => {
        const filters = condition.filters.map((f, i) => (i === idx ? { ...f, ...patch } : f));
        update({ filters });
    };

    return (
        <Box borderWidth="1px" borderRadius="md" p={3} bg="gray.50">
            <Flex gap={2} align="center" wrap="wrap">
                <Select
                    size="sm" maxW="180px" bg="white"
                    value={condition.occurrence}
                    onChange={(e) => update({ occurrence: e.target.value })}
                >
                    <option value="present">Event occurred</option>
                    <option value="absent">Event did NOT occur</option>
                </Select>
                <Select
                    size="sm" maxW="260px" bg="white" placeholder="Select eventâ€¦"
                    value={condition.event_name}
                    onChange={(e) => {
                        update({ event_name: e.target.value, filters: [] });
                        if (e.target.value) onNeedAttributes(e.target.value);
                    }}
                >
                    {events.map((ev) => (
                        <option key={ev.name} value={ev.name}>{ev.display_name || ev.name}</option>
                    ))}
                    {/* keep an unknown saved event visible (ANTI-12) */}
                    {condition.event_name && !events.some((ev) => ev.name === condition.event_name) && (
                        <option value={condition.event_name}>{condition.event_name}</option>
                    )}
                </Select>
                {condition.occurrence === 'present' && (
                    <HStack spacing={1}>
                        <Select
                            size="sm" maxW="110px" bg="white"
                            value={condition.count?.operator || 'at_least'}
                            onChange={(e) => update({ count: { operator: e.target.value, value: condition.count?.value ?? 1 } })}
                        >
                            <option value="at_least">at least</option>
                            <option value="at_most">at most</option>
                            <option value="exactly">exactly</option>
                        </Select>
                        <Input
                            size="sm" maxW="70px" bg="white" type="number" min={1} max={1000}
                            value={condition.count?.value ?? 1}
                            onChange={(e) => update({ count: { operator: condition.count?.operator || 'at_least', value: Number(e.target.value) } })}
                        />
                        <Text fontSize="xs" color="gray.500" whiteSpace="nowrap">times</Text>
                    </HStack>
                )}
                <Button
                    size="sm" variant="outline" leftIcon={<FiPlus />}
                    isDisabled={!condition.event_name}
                    onClick={() => update({ filters: [...condition.filters, { property: '', operator: 'equals', value: '' }] })}
                >
                    Property filter
                </Button>
                {removable && (
                    <IconButton size="sm" aria-label="Remove condition" icon={<FiTrash2 />} variant="ghost" onClick={onRemove} />
                )}
            </Flex>
            {condition.filters.length > 0 && (
                <VStack align="stretch" mt={2} spacing={2}>
                    {condition.filters.map((f, idx) => (
                        <Flex key={idx} gap={2} align="center" pl={6}>
                            <Text fontSize="xs" color="gray.500">where</Text>
                            <Select
                                size="sm" maxW="200px" bg="white" placeholder="propertyâ€¦"
                                value={f.property}
                                onChange={(e) => updateFilter(idx, { property: e.target.value })}
                            >
                                {(attributes[condition.event_name] || []).map((a) => (
                                    <option key={a.name} value={a.name}>{a.name}</option>
                                ))}
                                {f.property && !(attributes[condition.event_name] || []).some((a) => a.name === f.property) && (
                                    <option value={f.property}>{f.property}</option>
                                )}
                            </Select>
                            <Select
                                size="sm" maxW="150px" bg="white"
                                value={f.operator}
                                onChange={(e) => updateFilter(idx, { operator: e.target.value })}
                            >
                                {OPERATORS.map((op) => <option key={op.value} value={op.value}>{op.label}</option>)}
                            </Select>
                            <Input
                                size="sm" maxW="180px" bg="white" placeholder="value"
                                value={f.value}
                                onChange={(e) => updateFilter(idx, { value: e.target.value })}
                            />
                            <IconButton
                                size="xs" aria-label="Remove filter" icon={<FiTrash2 />} variant="ghost"
                                onClick={() => update({ filters: condition.filters.filter((_, i) => i !== idx) })}
                            />
                        </Flex>
                    ))}
                </VStack>
            )}
        </Box>
    );
}

/* â”€â”€â”€ Rule builder modal â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€ */
function RuleBuilderModal({ isOpen, onClose, initialRule, flows, events, attributes, onNeedAttributes, onSaved }) {
    const toast = useToast();
    const [rule, setRule] = useState(emptyRule());
    const [saving, setSaving] = useState(false);
    const [previewing, setPreviewing] = useState(false);
    const [preview, setPreview] = useState(null);
    const isEdit = Boolean(initialRule && initialRule._id);

    useEffect(() => {
        if (isOpen) {
            setPreview(null);
            const base = initialRule ? JSON.parse(JSON.stringify(initialRule)) : emptyRule();
            if (!base.groups || base.groups.length === 0) base.groups = [emptyGroup()];
            // Legacy rules stored one rule-level flow â€” prefill groups with it.
            base.groups = base.groups.map((g) => ({
                flow_id: g.flow_id || base.flow_id || '',
                flow_name: g.flow_name || base.flow_name || '',
                ...g,
                ...(g.flow_id ? {} : { flow_id: base.flow_id || '', flow_name: base.flow_name || '' }),
                parameters: (Array.isArray(g.parameters) && g.parameters.length > 0)
                    ? g.parameters
                    : ((g.flow_id || base.flow_id) ? [{ key: '', source: 'static', value: '', event_name: '', property: '' }] : []),
                session_conditions: Array.isArray(g.session_conditions) ? g.session_conditions : [],
            }));
            if (base.frequency_cap_days === undefined || base.frequency_cap_days === null) base.frequency_cap_days = 0;
            // Legacy rules stored only days â€” surface them as value+unit.
            if (!base.frequency_cap_value && base.frequency_cap_days > 0) base.frequency_cap_value = base.frequency_cap_days;
            if (!base.frequency_cap_unit) base.frequency_cap_unit = 'days';
            if (base.frequency_cap_value === undefined || base.frequency_cap_value === null) base.frequency_cap_value = 0;
            setRule(base);
            // hydrate attribute pickers for saved conditions + parameters
            (base.groups || []).forEach((g) => {
                (g.conditions || []).forEach((c) => { if (c.event_name) onNeedAttributes(c.event_name); });
                (g.parameters || []).forEach((p) => { if (p.event_name) onNeedAttributes(p.event_name); });
            });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isOpen, initialRule]);

    const updateGroup = (gi, group) => setRule({ ...rule, groups: rule.groups.map((g, i) => (i === gi ? group : g)) });

    /** Dry-run the current rule against the last 24h of ended sessions. */
    const runPreview = async () => {
        for (const g of rule.groups) {
            for (const c of g.conditions) {
                if (!c.event_name) return toast({ title: 'Every condition needs an event before previewing', status: 'warning' });
            }
        }
        setPreviewing(true);
        setPreview(null);
        try {
            const payload = {
                name: rule.name || 'preview', audience: rule.audience,
                frequency_cap_value: Number(rule.frequency_cap_value) || 0,
                frequency_cap_unit: rule.frequency_cap_unit || 'days',
                groups: rule.groups.map((g) => ({
                    conditions: g.conditions,
                    session_conditions: g.session_conditions || [],
                    flow_id: g.flow_id || '', flow_name: g.flow_name || '',
                    parameters: (g.parameters || []).filter((p) =>
                        String(p.key || '').trim() !== '' || String(p.value || '').trim() !== '' || p.event_name || p.property
                    ),
                })),
                hours: 24,
            };
            const res = await API.post('/session-analyzer/preview', payload, { headers: getApiHeaders(), timeout: 120000 });
            setPreview(res.data?.data || null);
        } catch (e) {
            toast({ title: 'Preview failed', description: e?.response?.data?.message || e.message, status: 'error' });
        } finally {
            setPreviewing(false);
        }
    };

    const save = async () => {
        if (!rule.name.trim()) return toast({ title: 'Rule name is required', status: 'warning' });
        for (const g of rule.groups) {
            if (!g.flow_id) return toast({ title: 'Every condition group needs a Trigger Flow', status: 'warning' });
            for (const sc of (g.session_conditions || [])) {
                if (sc.value === '' || sc.value === undefined || sc.value === null) {
                    return toast({ title: 'Every session condition needs a value', status: 'warning' });
                }
            }
            const activeParams = (g.parameters || []).filter((p) =>
                String(p.key || '').trim() !== '' || String(p.value || '').trim() !== '' || p.event_name || p.property
            );
            for (const p of activeParams) {
                if (!p.key || !String(p.key).trim()) return toast({ title: 'Every flow parameter needs a key', status: 'warning' });
                if (p.source === 'event_property') {
                    if (!p.event_name || !p.property) return toast({ title: `Parameter "${p.key}": pick an event and property`, status: 'warning' });
                } else if (p.source === 'session_field') {
                    if (!p.property) return toast({ title: `Parameter "${p.key}": pick a session field`, status: 'warning' });
                } else if (p.value === undefined || String(p.value).trim() === '') {
                    return toast({ title: `Parameter "${p.key}": value is required`, status: 'warning' });
                }
            }
            for (const c of g.conditions) {
                if (!c.event_name) return toast({ title: 'Every condition needs an event', status: 'warning' });
                for (const f of c.filters) {
                    if (!f.property || String(f.value).trim() === '') {
                        return toast({ title: 'Every property filter needs a property and value', status: 'warning' });
                    }
                }
            }
        }
        const payload = {
            name: rule.name, description: rule.description, audience: rule.audience,
            status: rule.status || 'active',
            frequency_cap_value: Number(rule.frequency_cap_value) || 0,
            frequency_cap_unit: rule.frequency_cap_unit || 'days',
            groups: rule.groups.map((g) => {
                const flow = flows.find((f) => f._id === g.flow_id);
                return {
                    conditions: g.conditions,
                    session_conditions: g.session_conditions || [],
                    flow_id: g.flow_id,
                    flow_name: flow ? flow.name : (g.flow_name || ''),
                    parameters: (g.parameters || []).filter((p) =>
                        String(p.key || '').trim() !== '' || String(p.value || '').trim() !== '' || p.event_name || p.property
                    ).map((p) => ({
                        key: String(p.key || '').trim(),
                        source: ['event_property', 'session_field'].includes(p.source) ? p.source : 'static',
                        value: ['event_property', 'session_field'].includes(p.source) ? '' : p.value,
                        event_name: p.source === 'event_property' ? p.event_name : '',
                        property: ['event_property', 'session_field'].includes(p.source) ? p.property : '',
                    })),
                };
            }),
        };
        setSaving(true);
        try {
            if (isEdit) {
                await API.put(`/session-analyzer/rules/${initialRule._id}`, payload, { headers: getApiHeaders() });
            } else {
                await API.post('/session-analyzer/rules', payload, { headers: getApiHeaders() });
            }
            toast({ title: isEdit ? 'Rule updated' : 'Rule created', status: 'success' });
            onSaved();
            onClose();
        } catch (e) {
            toast({ title: 'Save failed', description: e?.response?.data?.message || e.message, status: 'error' });
        } finally {
            setSaving(false);
        }
    };

    return (
        <Modal isOpen={isOpen} onClose={onClose} size="4xl" scrollBehavior="inside">
            <ModalOverlay />
            <ModalContent>
                <ModalHeader>{isEdit ? 'Edit Rule' : 'New Session Analyzer Rule'}</ModalHeader>
                <ModalCloseButton />
                <ModalBody>
                    <VStack align="stretch" spacing={4}>
                        <Flex gap={3} wrap="wrap">
                            <Box flex="1" minW="240px">
                                <Text fontSize="sm" mb={1} fontWeight="medium">Rule name</Text>
                                <Input value={rule.name} onChange={(e) => setRule({ ...rule, name: e.target.value })} placeholder="e.g. Cart abandoners" />
                            </Box>
                            <Box minW="200px">
                                <Text fontSize="sm" mb={1} fontWeight="medium">Audience</Text>
                                <Select value={rule.audience} onChange={(e) => setRule({ ...rule, audience: e.target.value })}>
                                    <option value="all">All sessions (incl. anonymous)</option>
                                    <option value="known">Known contacts only</option>
                                </Select>
                            </Box>
                        </Flex>
                            <Box>
                                <Text fontSize="sm" mb={1} fontWeight="medium">Frequency cap</Text>
                                <HStack spacing={2}>
                                    <Input
                                        type="number" min={0} maxW="90px"
                                        value={rule.frequency_cap_value ?? 0}
                                        onChange={(e) => setRule({ ...rule, frequency_cap_value: e.target.value === '' ? 0 : Number(e.target.value) })}
                                    />
                                    <Select
                                        maxW="130px"
                                        value={rule.frequency_cap_unit || 'days'}
                                        onChange={(e) => setRule({ ...rule, frequency_cap_unit: e.target.value })}
                                    >
                                        <option value="days">day(s)</option>
                                        <option value="weeks">week(s)</option>
                                        <option value="months">month(s)</option>
                                        <option value="sessions">session(s)</option>
                                    </Select>
                                </HStack>
                                <Text fontSize="xs" color="gray.500" mt={1}>
                                    {rule.frequency_cap_unit === 'sessions'
                                        ? (Number(rule.frequency_cap_value) > 1
                                            ? `Fires once every ${rule.frequency_cap_value} matching sessions per contact`
                                            : 'Fires on every matching session')
                                        : Number(rule.frequency_cap_value) > 0
                                            ? `At most once per contact every ${rule.frequency_cap_value} ${rule.frequency_cap_unit || 'days'}`
                                            : '0 = fire on every matching session'}
                                </Text>
                            </Box>
                        <Box>
                            <Text fontSize="sm" mb={1} fontWeight="medium">Description (optional)</Text>
                            <Textarea rows={2} value={rule.description} onChange={(e) => setRule({ ...rule, description: e.target.value })} />
                        </Box>
                        <Divider />
                        <Text fontSize="sm" color="gray.600">
                            The rule matches an ended session when <b>any</b> group below matches. A group matches when <b>all</b> of its conditions hold.
                        </Text>
                        {rule.groups.map((group, gi) => (
                            <Box key={gi} borderWidth="2px" borderColor="brand.100" borderRadius="lg" p={3}>
                                <Flex justify="space-between" align="center" mb={2}>
                                    <Tag colorScheme="brand">{gi === 0 ? 'WHEN' : `OR â€” Group ${gi + 1}`}</Tag>
                                    {rule.groups.length > 1 && (
                                        <IconButton
                                            size="sm" aria-label="Remove group" icon={<FiTrash2 />} variant="ghost"
                                            onClick={() => setRule({ ...rule, groups: rule.groups.filter((_, i) => i !== gi) })}
                                        />
                                    )}
                                </Flex>
                                <VStack align="stretch" spacing={2}>
                                    {group.conditions.map((cond, ci) => (
                                        <React.Fragment key={ci}>
                                            {ci > 0 && <Text fontSize="xs" fontWeight="bold" color="gray.500" pl={2}>AND</Text>}
                                            <ConditionRow
                                                condition={cond}
                                                events={events}
                                                attributes={attributes}
                                                onNeedAttributes={onNeedAttributes}
                                                removable={group.conditions.length > 1}
                                                onChange={(c) => updateGroup(gi, { ...group, conditions: group.conditions.map((x, i) => (i === ci ? c : x)) })}
                                                onRemove={() => updateGroup(gi, { ...group, conditions: group.conditions.filter((_, i) => i !== ci) })}
                                            />
                                        </React.Fragment>
                                    ))}
                                </VStack>
                                <HStack mt={2} spacing={2}>
                                    <Button
                                        size="sm" variant="outline" leftIcon={<FiPlus />}
                                        onClick={() => updateGroup(gi, { ...group, conditions: [...group.conditions, emptyCondition()] })}
                                    >
                                        AND condition
                                    </Button>
                                    <Button
                                        size="sm" variant="outline" leftIcon={<FiPlus />}
                                        isDisabled={(group.session_conditions || []).length >= 10}
                                        onClick={() => updateGroup(gi, { ...group, session_conditions: [...(group.session_conditions || []), { metric: 'duration_minutes', operator: 'greater_than', value: '' }] })}
                                    >
                                        Session condition
                                    </Button>
                                </HStack>
                                {(group.session_conditions || []).length > 0 && (
                                    <VStack align="stretch" mt={2} spacing={2}>
                                        {group.session_conditions.map((sc, si) => {
                                            const updateSc = (patch) => updateGroup(gi, {
                                                ...group,
                                                session_conditions: group.session_conditions.map((x, i) => (i === si ? { ...x, ...patch } : x)),
                                            });
                                            return (
                                                <Flex key={si} gap={2} align="center" pl={2} wrap="wrap">
                                                    <Text fontSize="xs" color="gray.500" whiteSpace="nowrap">AND session</Text>
                                                    <Select
                                                        size="sm" maxW="190px" bg="white"
                                                        value={sc.metric}
                                                        onChange={(e) => {
                                                            const ops = metricOperators(e.target.value).map((o) => o.value);
                                                            updateSc({ metric: e.target.value, operator: ops.includes(sc.operator) ? sc.operator : ops[0] });
                                                        }}
                                                    >
                                                        {SESSION_METRIC_OPTIONS.map((m) => <option key={m.value} value={m.value}>{m.label}</option>)}
                                                    </Select>
                                                    <Select
                                                        size="sm" maxW="150px" bg="white"
                                                        value={sc.operator}
                                                        onChange={(e) => updateSc({ operator: e.target.value })}
                                                    >
                                                        {metricOperators(sc.metric).map((op) => <option key={op.value} value={op.value}>{op.label}</option>)}
                                                    </Select>
                                                    {(sc.metric === 'entry_event' || sc.metric === 'exit_event') ? (
                                                        <Select
                                                            size="sm" maxW="240px" bg="white" placeholder="Select eventâ€¦"
                                                            value={sc.value ?? ''}
                                                            onChange={(e) => updateSc({ value: e.target.value })}
                                                        >
                                                            {events.map((ev) => (
                                                                <option key={ev.name} value={ev.name}>{ev.display_name || ev.name}</option>
                                                            ))}
                                                            {sc.value && !events.some((ev) => ev.name === sc.value) && (
                                                                <option value={sc.value}>{sc.value}</option>
                                                            )}
                                                        </Select>
                                                    ) : (
                                                        <Input
                                                            size="sm" maxW="180px" bg="white"
                                                            type={metricType(sc.metric) === 'numeric' ? 'number' : 'text'}
                                                            placeholder="value"
                                                            value={sc.value ?? ''}
                                                            onChange={(e) => updateSc({ value: e.target.value })}
                                                        />
                                                    )}
                                                    <IconButton
                                                        size="xs" aria-label="Remove session condition" icon={<FiTrash2 />} variant="ghost"
                                                        onClick={() => updateGroup(gi, { ...group, session_conditions: group.session_conditions.filter((_, i) => i !== si) })}
                                                    />
                                                </Flex>
                                            );
                                        })}
                                    </VStack>
                                )}
                                <Divider my={3} />
                                <Flex align="center" gap={2} wrap="wrap">
                                    <Tag colorScheme="brand" variant="subtle">THEN</Tag>
                                    <Text fontSize="sm" fontWeight="medium">Trigger Flow</Text>
                                    <Select
                                        size="sm" maxW="300px" bg="white" placeholder="Select flowâ€¦"
                                        value={group.flow_id || ''}
                                        onChange={(e) => {
                                            const flow = flows.find((f) => f._id === e.target.value);
                                            const seeded = (group.parameters && group.parameters.length > 0)
                                                ? group.parameters
                                                : (e.target.value ? [{ key: '', source: 'static', value: '', event_name: '', property: '' }] : []);
                                            updateGroup(gi, { ...group, flow_id: e.target.value, flow_name: flow ? flow.name : '', parameters: seeded });
                                        }}
                                    >
                                        {flows.map((f) => (
                                            <option key={f._id} value={f._id}>{f.name} {f.status !== 'active' ? `(${f.status})` : ''}</option>
                                        ))}
                                    </Select>
                                    <Text fontSize="xs" color="gray.500">Only flows with API Based Entry are listed</Text>
                                </Flex>
                                {group.flow_id && (
                                    <Box mt={3} pl={2}>
                                        <Flex align="center" gap={2} mb={2}>
                                            <Text fontSize="sm" fontWeight="medium">Flow parameters</Text>
                                            <Text fontSize="xs" color="gray.500">key/value pairs passed to the flow call</Text>
                                        </Flex>
                                        {(group.parameters || []).length > 0 && (
                                            <VStack align="stretch" spacing={2}>
                                                {group.parameters.map((param, pi) => {
                                                    const contractFields = (flows.find((f) => f._id === group.flow_id)?.contract_fields || []);
                                                    // Only events selected in THIS group's "occurred" conditions
                                                    // can supply property values (absent events never have any).
                                                    const groupEvents = [...new Set(group.conditions
                                                        .filter((c) => c.occurrence !== 'absent' && c.event_name)
                                                        .map((c) => c.event_name))];
                                                    const updateParam = (patch) => updateGroup(gi, {
                                                        ...group,
                                                        parameters: group.parameters.map((x, i) => (i === pi ? { ...x, ...patch } : x)),
                                                    });
                                                    return (
                                                        <Flex key={pi} gap={2} align="center" wrap="wrap">
                                                            <Input
                                                                size="sm" maxW="180px" bg="white" placeholder="key"
                                                                value={param.key}
                                                                list={`sa-contract-${gi}`}
                                                                onChange={(e) => updateParam({ key: e.target.value })}
                                                            />
                                                            <datalist id={`sa-contract-${gi}`}>
                                                                {contractFields.map((cf) => (
                                                                    <option key={cf.name || cf} value={cf.name || cf} />
                                                                ))}
                                                            </datalist>
                                                            <Text fontSize="xs" color="gray.500">=</Text>
                                                            <Select
                                                                size="sm" maxW="180px" bg="white"
                                                                value={param.source}
                                                                onChange={(e) => updateParam({ source: e.target.value, event_name: '', property: '' })}
                                                            >
                                                                <option value="static">Static value</option>
                                                                <option value="event_property">Event property</option>
                                                                <option value="session_field">Session field (auto-tracked)</option>
                                                            </Select>
                                                            {param.source === 'session_field' ? (
                                                                <Select
                                                                    size="sm" maxW="240px" bg="white" placeholder="Select fieldâ€¦"
                                                                    value={param.property || ''}
                                                                    onChange={(e) => updateParam({ property: e.target.value })}
                                                                >
                                                                    {SESSION_FIELD_OPTIONS.map((f) => (
                                                                        <option key={f.value} value={f.value}>{f.label}</option>
                                                                    ))}
                                                                </Select>
                                                            ) : param.source === 'event_property' ? (
                                                                <>
                                                                    <Select
                                                                        size="sm" maxW="200px" bg="white" placeholder="eventâ€¦"
                                                                        value={param.event_name || ''}
                                                                        onChange={(e) => {
                                                                            updateParam({ event_name: e.target.value, property: '' });
                                                                            if (e.target.value) onNeedAttributes(e.target.value);
                                                                        }}
                                                                    >
                                                                        {groupEvents.map((evName) => (
                                                                            <option key={evName} value={evName}>{evName}</option>
                                                                        ))}
                                                                        {param.event_name && !groupEvents.includes(param.event_name) && (
                                                                            <option value={param.event_name}>{param.event_name}</option>
                                                                        )}
                                                                    </Select>
                                                                    <Select
                                                                        size="sm" maxW="200px" bg="white" placeholder="propertyâ€¦"
                                                                        value={param.property || ''}
                                                                        onChange={(e) => updateParam({ property: e.target.value })}
                                                                    >
                                                                        {(attributes[param.event_name] || []).map((a) => (
                                                                            <option key={a.name} value={a.name}>{a.name}</option>
                                                                        ))}
                                                                        {param.property && !(attributes[param.event_name] || []).some((a) => a.name === param.property) && (
                                                                            <option value={param.property}>{param.property}</option>
                                                                        )}
                                                                    </Select>
                                                                </>
                                                            ) : (
                                                                <Input
                                                                    size="sm" maxW="220px" bg="white" placeholder="value"
                                                                    value={param.value ?? ''}
                                                                    onChange={(e) => updateParam({ value: e.target.value })}
                                                                />
                                                            )}
                                                            <IconButton
                                                                size="xs" aria-label="Remove parameter" icon={<FiTrash2 />} variant="ghost"
                                                                onClick={() => updateGroup(gi, { ...group, parameters: group.parameters.filter((_, i) => i !== pi) })}
                                                            />
                                                        </Flex>
                                                    );
                                                })}
                                            </VStack>
                                        )}
                                        <Button
                                            size="sm" mt={2} variant="outline" leftIcon={<FiPlus />}
                                            isDisabled={(group.parameters || []).length >= 20}
                                            onClick={() => updateGroup(gi, { ...group, parameters: [...(group.parameters || []), { key: '', source: 'static', value: '', event_name: '', property: '' }] })}
                                        >
                                            Add parameter
                                        </Button>
                                    </Box>
                                )}
                            </Box>
                        ))}
                        <Button
                            variant="outline" colorScheme="brand" leftIcon={<FiPlus />} alignSelf="flex-start"
                            isDisabled={rule.groups.length >= 10}
                            onClick={() => setRule({ ...rule, groups: [...rule.groups, emptyGroup()] })}
                        >
                            OR group
                        </Button>
                        {preview && (
                            <Box borderWidth="1px" borderColor="brand.200" bg="brand.50" borderRadius="md" p={3}>
                                <Text fontSize="sm" fontWeight="bold" color="brand.800">
                                    Preview â€” last 24h of ended sessions: {preview.matched} of {preview.scanned} would match
                                </Text>
                                {preview.matched === 0 && (
                                    <Text fontSize="xs" color="brand.700" mt={1}>
                                        No matching sessions in the window. Nothing would have fired.
                                    </Text>
                                )}
                                {(preview.matches || []).length > 0 && (
                                    <TableContainer mt={2} maxH="220px" overflowY="auto">
                                        <Table size="sm" bg="white" borderRadius="md">
                                            <Thead>
                                                <Tr><Th>Session</Th><Th>Contact</Th><Th>Ended</Th><Th>Group</Th><Th>Parameters</Th></Tr>
                                            </Thead>
                                            <Tbody>
                                                {preview.matches.map((m) => (
                                                    <Tr key={m.session_id}>
                                                        <Td fontSize="xs" fontFamily="mono" maxW="170px" overflow="hidden" textOverflow="ellipsis">{m.session_id}</Td>
                                                        <Td fontSize="xs">{m.contact || <Text as="span" color="gray.400">anonymous</Text>}</Td>
                                                        <Td fontSize="xs">{m.ended_at}</Td>
                                                        <Td fontSize="xs">{(m.matched_group ?? 0) + 1}</Td>
                                                        <Td fontSize="xs" fontFamily="mono" maxW="240px" overflow="hidden" textOverflow="ellipsis">
                                                            {JSON.stringify(m.parameters || {})}
                                                        </Td>
                                                    </Tr>
                                                ))}
                                            </Tbody>
                                        </Table>
                                    </TableContainer>
                                )}
                            </Box>
                        )}
                    </VStack>
                </ModalBody>
                <ModalFooter>
                    <Button
                        variant="outline" colorScheme="brand" leftIcon={<FiEye />} mr="auto"
                        onClick={runPreview} isLoading={previewing} loadingText="Previewingâ€¦"
                    >
                        Preview (24h dry run)
                    </Button>
                    <Button variant="ghost" mr={3} onClick={onClose}>Cancel</Button>
                    <Button colorScheme="brand" onClick={save} isLoading={saving}>{isEdit ? 'Save changes' : 'Create rule'}</Button>
                </ModalFooter>
            </ModalContent>
        </Modal>
    );
}

/* â”€â”€â”€ Page â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€ */
export default function SessionAnalyzer({ header }) {
    const toast = useToast();
    const navigate = useNavigate();
    const builder = useDisclosure();
    const [selectedTrigger, setSelectedTrigger] = useState(null);
    const [rules, setRules] = useState([]);
    const [sweeper, setSweeper] = useState(null);
    const [rulesLoading, setRulesLoading] = useState(true);
    const [editingRule, setEditingRule] = useState(null);
    const [deleteTarget, setDeleteTarget] = useState(null);
    const [runNowBusy, setRunNowBusy] = useState(false);
    const cancelRef = React.useRef();

    const [flows, setFlows] = useState([]);
    const [events, setEvents] = useState([]);
    const [attributes, setAttributes] = useState({}); // event_name â†’ [{name,type}]

    const [testerRuleId, setTesterRuleId] = useState('');
    const [testerHours, setTesterHours] = useState(24);
    const [testerLoading, setTesterLoading] = useState(false);
    const [testerResult, setTesterResult] = useState(null);
    const [testerExpanded, setTesterExpanded] = useState(null);

    const [triggers, setTriggers] = useState([]);
    const [triggersLoading, setTriggersLoading] = useState(false);
    const [triggerPage, setTriggerPage] = useState(1);
    const [triggerTotal, setTriggerTotal] = useState(0);
    const [triggerRuleFilter, setTriggerRuleFilter] = useState('');
    const TRIGGER_LIMIT = 20;

    const loadRules = useCallback(async () => {
        setRulesLoading(true);
        try {
            const res = await API.get('/session-analyzer/rules?limit=100', { headers: getApiHeaders() });
            setRules(res.data?.data?.rules || []);
            setSweeper(res.data?.data?.sweeper || null);
        } catch (e) {
            toast({ title: 'Failed to load rules', description: e?.response?.data?.message || e.message, status: 'error' });
        } finally {
            setRulesLoading(false);
        }
    }, [toast]);

    const loadPickers = useCallback(async () => {
        try {
            const [flowRes, eventRes] = await Promise.all([
                // Only flows with an API Based Entry start node are eligible targets.
                API.get('/session-analyzer/flows', { headers: getApiHeaders() }),
                API.post('/fetch-unique-events', {}, { headers: getApiHeaders(), timeout: 20000 }),
            ]);
            setFlows(flowRes.data?.data?.flows || []);
            setEvents(eventRes.data?.data?.events || []);
        } catch (e) {
            toast({ title: 'Failed to load flows/events', description: e?.response?.data?.message || e.message, status: 'error' });
        }
    }, [toast]);

    const loadAttributes = useCallback(async (eventName) => {
        if (!eventName) return;
        setAttributes((prev) => {
            if (prev[eventName]) return prev;
            // mark as loading with empty list, fetch async
            API.post('/fetch-unique-attributes', { event: eventName }, { headers: getApiHeaders() })
                .then((res) => {
                    const attrs = res.data?.data?.attributes || [];
                    setAttributes((p) => ({ ...p, [eventName]: attrs }));
                })
                .catch(() => setAttributes((p) => ({ ...p, [eventName]: [] })));
            return { ...prev, [eventName]: prev[eventName] || [] };
        });
    }, []);

    const loadTriggers = useCallback(async (page = 1, ruleId = triggerRuleFilter) => {
        setTriggersLoading(true);
        try {
            const q = new URLSearchParams({ page: String(page), limit: String(TRIGGER_LIMIT) });
            if (ruleId) q.set('rule_id', ruleId);
            const res = await API.get(`/session-analyzer/triggers?${q.toString()}`, { headers: getApiHeaders() });
            setTriggers(res.data?.data?.triggers || []);
            setTriggerTotal(res.data?.data?.pagination?.total || 0);
            setTriggerPage(page);
        } catch (e) {
            toast({ title: 'Failed to load trigger history', description: e?.response?.data?.message || e.message, status: 'error' });
        } finally {
            setTriggersLoading(false);
        }
    }, [toast, triggerRuleFilter]);

    useEffect(() => { loadRules(); loadPickers(); }, [loadRules, loadPickers]);

    const toggleStatus = async (rule) => {
        const status = rule.status === 'active' ? 'paused' : 'active';
        try {
            await API.post(`/session-analyzer/rules/${rule._id}/status`, { status }, { headers: getApiHeaders() });
            toast({ title: `Rule ${status === 'active' ? 'activated' : 'paused'}`, status: 'success' });
            loadRules();
        } catch (e) {
            toast({ title: 'Status change failed', description: e?.response?.data?.message || e.message, status: 'error' });
        }
    };

    const confirmDelete = async () => {
        if (!deleteTarget) return;
        try {
            await API.delete(`/session-analyzer/rules/${deleteTarget._id}`, { headers: getApiHeaders() });
            toast({ title: 'Rule deleted', status: 'success' });
            setDeleteTarget(null);
            loadRules();
        } catch (e) {
            toast({ title: 'Delete failed', description: e?.response?.data?.message || e.message, status: 'error' });
        }
    };

    /** Rule Tester â€” pure simulation: nothing fires, nothing is posted to xFlow.
     *  With no rule selected, tests ALL rules and groups results per rule. */
    const runTester = async () => {
        setTesterLoading(true);
        setTesterResult(null);
        setTesterExpanded(null);
        try {
            if (testerRuleId) {
                const res = await API.post(`/session-analyzer/rules/${testerRuleId}/test`, { hours: Number(testerHours) }, { headers: getApiHeaders(), timeout: 120000 });
                setTesterResult({ mode: 'single', ...(res.data?.data || {}) });
            } else {
                const res = await API.post('/session-analyzer/test-all', { hours: Number(testerHours) }, { headers: getApiHeaders(), timeout: 180000 });
                setTesterResult({ mode: 'all', ...(res.data?.data || {}) });
            }
        } catch (e) {
            toast({ title: 'Rule test failed', description: e?.response?.data?.message || e.message, status: 'error' });
        } finally {
            setTesterLoading(false);
        }
    };

    /** Shared results table for the tester (single + all modes). */
    const renderTesterTable = (results, prefix) => (
        <TableContainer>
            <Table size="sm" variant="simple">
                <Thead>
                    <Tr>
                        <Th>Session</Th>
                        <Th>Subscriber ID</Th>
                        <Th>Contact</Th>
                        <Th>Ended</Th>
                        <Th>Flow to call</Th>
                        <Th>Outcome</Th>
                    </Tr>
                </Thead>
                <Tbody>
                    {results.map((r, i) => {
                        const key = `${prefix}-${i}`;
                        return (
                            <React.Fragment key={key}>
                                <Tr onClick={() => setTesterExpanded(testerExpanded === key ? null : key)} cursor="pointer" _hover={{ bg: 'gray.50' }}>
                                    <Td fontSize="xs" fontFamily="mono" maxW="180px" overflow="hidden" textOverflow="ellipsis">{r.session_id}</Td>
                                    <Td fontSize="xs" fontFamily="mono" maxW="160px" overflow="hidden" textOverflow="ellipsis">{r.subscriber_id || 'â€”'}</Td>
                                    <Td fontSize="xs">{r.contact || <Text as="span" color="gray.400">anonymous</Text>}</Td>
                                    <Td fontSize="xs">{r.ended_at}</Td>
                                    <Td><Tag size="sm" colorScheme="brand">{r.flow?.name || 'â€”'}</Tag></Td>
                                    <Td>
                                        {r.would_fire
                                            ? <Tag colorScheme="green">would fire</Tag>
                                            : <Tooltip label={r.block_reason}><Tag colorScheme="orange">blocked</Tag></Tooltip>}
                                    </Td>
                                </Tr>
                                {testerExpanded === key && (
                                    <Tr>
                                        <Td colSpan={6} bg="gray.50" p={3}>
                                            {!r.would_fire && (
                                                <Text fontSize="xs" color="orange.600" mb={2} fontWeight="medium">Blocked: {r.block_reason}</Text>
                                            )}
                                            <Text fontSize="xs" fontWeight="bold" mb={1}>
                                                Data that would be posted to xFlow ({r.flow?.name}):
                                            </Text>
                                            <Box as="pre" fontSize="xs" fontFamily="mono" bg="white" p={3} borderRadius="md" borderWidth="1px" overflowX="auto" maxH="320px" overflowY="auto">
                                                {r.xflow_post ? JSON.stringify(r.xflow_post, null, 2) : 'No payload â€” flow missing or misconfigured'}
                                            </Box>
                                        </Td>
                                    </Tr>
                                )}
                            </React.Fragment>
                        );
                    })}
                </Tbody>
            </Table>
        </TableContainer>
    );

    const runNow = async () => {
        setRunNowBusy(true);
        try {
            const res = await API.post('/session-analyzer/run', {}, { headers: getApiHeaders(), timeout: 120000 });
            const s = res.data?.data?.stats || {};
            toast({
                title: 'Sweep complete',
                description: `sessions: ${s.sessions ?? 0} Â· fired: ${s.fired ?? 0} Â· deduped: ${s.deduped ?? 0}`,
                status: 'success',
            });
            loadRules();
        } catch (e) {
            toast({ title: 'Sweep failed', description: e?.response?.data?.message || e.message, status: 'error' });
        } finally {
            setRunNowBusy(false);
        }
    };

    const summarizeRule = (rule) => (rule.groups || []).map((g, i) => {
        const parts = (g.conditions || []).map((c) => `${c.occurrence === 'absent' ? 'NOT ' : ''}${c.event_name}${c.filters?.length ? ` (${c.filters.length} filter${c.filters.length > 1 ? 's' : ''})` : ''}`);
        const flowTag = g.flow_name || g.flow_id ? ` â†’ ${g.flow_name || g.flow_id}` : '';
        return `${i > 0 ? 'OR ' : ''}[${parts.join(' AND ')}${flowTag}]`;
    }).join(' ');

    return (
        <>
            <Header header={header} />
            <BoxWrapper>
                <Flex justify="space-between" align="center" mb={4} wrap="wrap" gap={2}>
                    <Box>
                        <Text fontSize="xl" fontWeight="bold">Session Analyzer</Text>
                        <Text fontSize="sm" color="gray.500">
                            When a session ends (30 min inactivity), matching rules trigger a Flow.
                            {sweeper?.last_sweep_at ? ` Â· Last sweep: ${fmtTs(sweeper.last_sweep_at)}` : ' Â· Sweeper has not run yet'}
                        </Text>
                    </Box>
                    <HStack>
                        <Button leftIcon={<FiRefreshCw />} variant="outline" size="sm" onClick={runNow} isLoading={runNowBusy} loadingText="Sweepingâ€¦">
                            Run now
                        </Button>
                        <Button leftIcon={<FiPlus />} colorScheme="brand" size="sm" onClick={() => { setEditingRule(null); builder.onOpen(); }}>
                            New Rule
                        </Button>
                    </HStack>
                </Flex>

                <Tabs colorScheme="brand" onChange={(i) => { if (i === 1) loadTriggers(1); }}>
                    <TabList>
                        <Tab>Rules</Tab>
                        <Tab>Trigger History</Tab>
                        <Tab>Rule Tester</Tab>
                    </TabList>
                    <TabPanels>
                        <TabPanel px={0}>
                            {rulesLoading ? (
                                <Flex justify="center" py={10}><Spinner color="var(--brand-primary)" /></Flex>
                            ) : rules.length === 0 ? (
                                <VStack py={10} spacing={3}>
                                    <FiZap size={28} color="var(--brand-primary)" />
                                    <Text fontWeight="medium">No rules yet</Text>
                                    <Text fontSize="sm" color="gray.500" textAlign="center" maxW="420px">
                                        Create your first rule â€” e.g. â€œproduct_viewed occurred AND added_cart did NOT occur â†’ trigger the Cart Recovery flowâ€.
                                    </Text>
                                    <Button leftIcon={<FiPlus />} colorScheme="brand" size="sm" onClick={() => { setEditingRule(null); builder.onOpen(); }}>
                                        New Rule
                                    </Button>
                                </VStack>
                            ) : (
                                <TableContainer>
                                    <Table size="sm" variant="simple">
                                        <Thead>
                                            <Tr>
                                                <Th>Rule</Th>
                                                <Th>Conditions</Th>
                                                <Th>Flow</Th>
                                                <Th>Audience</Th>
                                                <Th>Status</Th>
                                                <Th isNumeric>Matched</Th>
                                                <Th>Last matched</Th>
                                                <Th>Actions</Th>
                                            </Tr>
                                        </Thead>
                                        <Tbody>
                                            {rules.map((rule) => (
                                                <Tr key={rule._id}>
                                                    <Td fontWeight="medium">{rule.name}</Td>
                                                    <Td maxW="340px">
                                                        <Tooltip label={summarizeRule(rule)}>
                                                            <Text fontSize="xs" color="gray.600" noOfLines={2}>{summarizeRule(rule)}</Text>
                                                        </Tooltip>
                                                    </Td>
                                                    <Td>
                                                        <HStack spacing={1} wrap="wrap">
                                                            {[...new Set((rule.groups || []).map((g) => g.flow_name || g.flow_id).filter(Boolean))].length > 0
                                                                ? [...new Set((rule.groups || []).map((g) => g.flow_name || g.flow_id).filter(Boolean))].map((fn) => (
                                                                    <Tag key={fn} size="sm" colorScheme="brand">{fn}</Tag>
                                                                ))
                                                                : <Tag size="sm" colorScheme="brand">{rule.flow_name || rule.flow_id || 'â€”'}</Tag>}
                                                        </HStack>
                                                    </Td>
                                                    <Td>
                                                        <Tag colorScheme={rule.audience === 'known' ? 'brand' : 'gray'}>
                                                            {rule.audience === 'known' ? 'Known contacts' : 'All sessions'}
                                                        </Tag>
                                                    </Td>
                                                    <Td>
                                                        <Tag colorScheme={rule.status === 'active' ? 'green' : 'yellow'}>{rule.status}</Tag>
                                                    </Td>
                                                    <Td isNumeric>{rule.stats?.matched_count ?? 0}</Td>
                                                    <Td fontSize="xs">{fmtTs(rule.stats?.last_matched_at)}</Td>
                                                    <Td>
                                                        <HStack spacing={1}>
                                                            <Tooltip label="Edit">
                                                                <IconButton size="xs" aria-label="Edit" icon={<FiEdit2 />} onClick={() => { setEditingRule(rule); builder.onOpen(); }} />
                                                            </Tooltip>
                                                            <Tooltip label={rule.status === 'active' ? 'Pause' : 'Activate'}>
                                                                <IconButton size="xs" aria-label="Toggle" icon={rule.status === 'active' ? <FiPause /> : <FiPlay />} onClick={() => toggleStatus(rule)} />
                                                            </Tooltip>
                                                            <Tooltip label="Delete">
                                                                <IconButton size="xs" aria-label="Delete" colorScheme="red" variant="ghost" icon={<FiTrash2 />} onClick={() => setDeleteTarget(rule)} />
                                                            </Tooltip>
                                                        </HStack>
                                                    </Td>
                                                </Tr>
                                            ))}
                                        </Tbody>
                                    </Table>
                                </TableContainer>
                            )}
                        </TabPanel>

                        <TabPanel px={0}>
                            <Flex mb={3} gap={2} align="center">
                                <Select
                                    size="sm" maxW="280px" placeholder="All rules"
                                    value={triggerRuleFilter}
                                    onChange={(e) => { setTriggerRuleFilter(e.target.value); loadTriggers(1, e.target.value); }}
                                >
                                    {rules.map((r) => <option key={r._id} value={r._id}>{r.name}</option>)}
                                </Select>
                                <IconButton size="sm" aria-label="Refresh" icon={<FiRefreshCw />} onClick={() => loadTriggers(triggerPage)} />
                            </Flex>
                            {triggersLoading ? (
                                <Flex justify="center" py={10}><Spinner color="var(--brand-primary)" /></Flex>
                            ) : triggers.length === 0 ? (
                                <VStack py={10}>
                                    <Text fontWeight="medium">No triggers yet</Text>
                                    <Text fontSize="sm" color="gray.500">Fired rules will appear here with the session that matched.</Text>
                                </VStack>
                            ) : (
                                <>
                                    <TableContainer>
                                        <Table size="sm" variant="simple">
                                            <Thead>
                                                <Tr>
                                                    <Th>When</Th>
                                                    <Th>Rule</Th>
                                                    <Th>Session</Th>
                                                    <Th>Contact</Th>
                                                    <Th>Flow</Th>
                                                    <Th>Status</Th>
                                                </Tr>
                                            </Thead>
                                            <Tbody>
                                                {triggers.map((t) => (
                                                    <Tr key={t._id} onClick={() => setSelectedTrigger(t)} cursor="pointer" _hover={{ bg: 'gray.50' }}>
                                                        <Td fontSize="xs">{fmtTs(t.created_at)}</Td>
                                                        <Td>{t.rule_name}</Td>
                                                        <Td fontSize="xs" fontFamily="mono" maxW="200px" overflow="hidden" textOverflow="ellipsis">{t.session_id}</Td>
                                                        <Td fontSize="xs">{t.contact || <Text as="span" color="gray.400">anonymous</Text>}</Td>
                                                        <Td><Tag size="sm" colorScheme="brand">{t.flow_name || t.flow_id}</Tag></Td>
                                                        <Td>
                                                            <Tooltip label={t.error || ''} isDisabled={!t.error}>
                                                                <Tag colorScheme={STATUS_COLORS[t.status] || 'gray'}>{t.status}</Tag>
                                                            </Tooltip>
                                                        </Td>
                                                    </Tr>
                                                ))}
                                            </Tbody>
                                        </Table>
                                    </TableContainer>
                                    {triggerTotal > TRIGGER_LIMIT && (
                                        <Flex justify="center" mt={3}>
                                            <Pagination
                                                activePage={triggerPage}
                                                itemsCountPerPage={TRIGGER_LIMIT}
                                                totalItemsCount={triggerTotal}
                                                pageRangeDisplayed={5}
                                                onChange={(p) => loadTriggers(p)}
                                                itemClass="page-item"
                                                linkClass="page-link"
                                            />
                                        </Flex>
                                    )}
                                </>
                            )}
                        </TabPanel>
                        <TabPanel px={0}>
                            <Flex gap={2} align="center" mb={1} wrap="wrap">
                                <Select
                                    maxW="340px" size="sm" placeholder="All rules"
                                    value={testerRuleId}
                                    onChange={(e) => { setTesterRuleId(e.target.value); setTesterResult(null); }}
                                >
                                    {rules.map((r) => <option key={r._id} value={r._id}>{r.name} ({r.status})</option>)}
                                </Select>
                                <Select maxW="200px" size="sm" value={testerHours} onChange={(e) => setTesterHours(e.target.value)}>
                                    <option value={1}>Last 1 hour</option>
                                    <option value={6}>Last 6 hours</option>
                                    <option value={24}>Last 24 hours</option>
                                    <option value={72}>Last 3 days</option>
                                    <option value={168}>Last 7 days</option>
                                </Select>
                                <Button size="sm" colorScheme="brand" leftIcon={<FiPlay />} onClick={runTester} isLoading={testerLoading} loadingText="Testingâ€¦">
                                    Run test
                                </Button>
                            </Flex>
                            <Text fontSize="xs" color="gray.500" mb={3}>
                                Simulation only â€” nothing is posted to xFlow, no triggers are recorded. Shows exactly what WOULD happen.
                            </Text>
                            {testerResult && testerResult.mode === 'all' && (
                                <>
                                    <Flex gap={4} mb={3} wrap="wrap">
                                        <Tag px={2} py={1}>{testerResult.scanned} ended sessions scanned</Tag>
                                        <Tag px={2} py={1} colorScheme="brand">{testerResult.rules_tested} rules tested</Tag>
                                        <Tag px={2} py={1} colorScheme="green">
                                            {(testerResult.rules || []).filter((r) => r.would_fire > 0).length} rules would trigger
                                        </Tag>
                                    </Flex>
                                    <VStack align="stretch" spacing={4}>
                                        {(testerResult.rules || []).map((rr, ri) => (
                                            <Box key={rr.rule._id} borderWidth="1px" borderRadius="lg" p={3}
                                                borderColor={rr.would_fire > 0 ? 'green.200' : 'gray.200'}>
                                                <Flex gap={2} align="center" mb={rr.matched > 0 ? 2 : 0} wrap="wrap">
                                                    <Text fontWeight="bold">{rr.rule.name}</Text>
                                                    <Tag colorScheme={rr.rule.status === 'active' ? 'green' : 'yellow'}>{rr.rule.status}</Tag>
                                                    <Tag colorScheme="brand">{rr.matched} matched</Tag>
                                                    <Tag colorScheme={rr.would_fire > 0 ? 'green' : 'gray'}>{rr.would_fire} would fire</Tag>
                                                    {rr.matched - rr.would_fire > 0 && <Tag colorScheme="orange">{rr.matched - rr.would_fire} blocked</Tag>}
                                                </Flex>
                                                {rr.matched === 0
                                                    ? <Text fontSize="sm" color="gray.500">No matching sessions in this window.</Text>
                                                    : renderTesterTable(rr.results, `r${ri}`)}
                                            </Box>
                                        ))}
                                    </VStack>
                                </>
                            )}
                            {testerResult && testerResult.mode === 'single' && (
                                <>
                                    <Flex gap={4} mb={3} wrap="wrap">
                                        <Tag px={2} py={1}>{testerResult.scanned} ended sessions scanned</Tag>
                                        <Tag px={2} py={1} colorScheme="brand">{testerResult.matched} matched</Tag>
                                        <Tag px={2} py={1} colorScheme="green">{testerResult.would_fire} would fire</Tag>
                                        <Tag px={2} py={1} colorScheme="orange">{testerResult.matched - testerResult.would_fire} blocked</Tag>
                                    </Flex>
                                    {(testerResult.results || []).length === 0 ? (
                                        <VStack py={8}>
                                            <Text fontWeight="medium">No matching sessions in this window</Text>
                                            <Text fontSize="sm" color="gray.500">Try a wider window, or check the rule's conditions against recent activity.</Text>
                                        </VStack>
                                    ) : (
                                        renderTesterTable(testerResult.results, 's')
                                    )}
                                </>
                            )}
                        </TabPanel>
                    </TabPanels>
                </Tabs>
            </BoxWrapper>

            <RuleBuilderModal
                isOpen={builder.isOpen}
                onClose={builder.onClose}
                initialRule={editingRule}
                flows={flows}
                events={events}
                attributes={attributes}
                onNeedAttributes={loadAttributes}
                onSaved={loadRules}
            />

            <Drawer isOpen={Boolean(selectedTrigger)} placement="right" size="md" onClose={() => setSelectedTrigger(null)}>
                <DrawerOverlay />
                <DrawerContent>
                    <DrawerCloseButton />
                    <DrawerHeader>
                        Trigger detail
                        <Tag ml={2} colorScheme={STATUS_COLORS[selectedTrigger?.status] || 'gray'}>{selectedTrigger?.status}</Tag>
                    </DrawerHeader>
                    <DrawerBody>
                        {selectedTrigger && (
                            <VStack align="stretch" spacing={3} fontSize="sm">
                                <Box>
                                    <Text fontWeight="bold">{selectedTrigger.rule_name}</Text>
                                    <Text fontSize="xs" color="gray.500">fired {fmtTs(selectedTrigger.created_at)} Â· matched group {(selectedTrigger.matched_group ?? 0) + 1}</Text>
                                </Box>
                                {selectedTrigger.error && (
                                    <Box bg="red.50" borderWidth="1px" borderColor="red.200" borderRadius="md" p={2}>
                                        <Text fontSize="xs" color="red.700">{selectedTrigger.error}</Text>
                                    </Box>
                                )}
                                <Divider />
                                <Flex justify="space-between"><Text color="gray.500">Flow</Text><Tag size="sm" colorScheme="brand">{selectedTrigger.flow_name || selectedTrigger.flow_id || 'â€”'}</Tag></Flex>
                                {selectedTrigger.execution_id && (
                                    <Flex justify="space-between" gap={3}>
                                        <Text color="gray.500">Execution</Text>
                                        <Text fontSize="xs" fontFamily="mono">{String(selectedTrigger.execution_id)}</Text>
                                    </Flex>
                                )}
                                <Flex justify="space-between"><Text color="gray.500">Contact</Text><Text>{selectedTrigger.contact || 'anonymous'}</Text></Flex>
                                {selectedTrigger.distinct_id && (
                                    <Flex justify="space-between"><Text color="gray.500">Distinct ID</Text><Text fontSize="xs" fontFamily="mono">{selectedTrigger.distinct_id}</Text></Flex>
                                )}
                                <Divider />
                                <Box>
                                    <Text fontWeight="medium" mb={1}>Session</Text>
                                    <Flex justify="space-between"><Text color="gray.500">Session ID</Text><Text fontSize="xs" fontFamily="mono">{selectedTrigger.session_id}</Text></Flex>
                                    <Flex justify="space-between"><Text color="gray.500">Started</Text><Text fontSize="xs">{fmtTs(selectedTrigger.session?.started_at)}</Text></Flex>
                                    <Flex justify="space-between"><Text color="gray.500">Ended</Text><Text fontSize="xs">{fmtTs(selectedTrigger.session?.ended_at)}</Text></Flex>
                                    <Flex justify="space-between"><Text color="gray.500">Events</Text><Text fontSize="xs">{selectedTrigger.session?.total_events ?? 'â€”'}</Text></Flex>
                                </Box>
                                {selectedTrigger.resolved_parameters && Object.keys(selectedTrigger.resolved_parameters).length > 0 && (
                                    <>
                                        <Divider />
                                        <Box>
                                            <Text fontWeight="medium" mb={1}>Parameters sent to flow</Text>
                                            <TableContainer>
                                                <Table size="sm">
                                                    <Tbody>
                                                        {Object.entries(selectedTrigger.resolved_parameters).map(([k, v]) => (
                                                            <Tr key={k}>
                                                                <Td fontSize="xs" fontWeight="medium" pl={0}>{k}</Td>
                                                                <Td fontSize="xs" fontFamily="mono">{String(v)}</Td>
                                                            </Tr>
                                                        ))}
                                                    </Tbody>
                                                </Table>
                                            </TableContainer>
                                        </Box>
                                    </>
                                )}
                                <Divider />
                                <Button
                                    leftIcon={<FiExternalLink />} size="sm" variant="outline" colorScheme="brand"
                                    onClick={() => navigate(`/marketing/session-journey?session_id=${encodeURIComponent(selectedTrigger.session_id)}`)}
                                >
                                    Open Session Journey
                                </Button>
                            </VStack>
                        )}
                    </DrawerBody>
                </DrawerContent>
            </Drawer>

            <AlertDialog isOpen={Boolean(deleteTarget)} leastDestructiveRef={cancelRef} onClose={() => setDeleteTarget(null)}>
                <AlertDialogOverlay>
                    <AlertDialogContent>
                        <AlertDialogHeader>Delete rule</AlertDialogHeader>
                        <AlertDialogBody>
                            Delete â€œ{deleteTarget?.name}â€? It will stop evaluating sessions. Trigger history is kept.
                        </AlertDialogBody>
                        <AlertDialogFooter>
                            <Button ref={cancelRef} onClick={() => setDeleteTarget(null)}>Cancel</Button>
                            <Button colorScheme="red" ml={3} onClick={confirmDelete}>Delete</Button>
                        </AlertDialogFooter>
                    </AlertDialogContent>
                </AlertDialogOverlay>
            </AlertDialog>
        </>
    );
}
